import { inject, Injectable, Injector, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IOption, OptionCode } from '@cardium/contracts';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OptionStore {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly baseUrl = 'api/core/Option';

  private readonly optionCache = new Map<string, Observable<Partial<IOption> | null>>();
  private readonly optionsListCache = new Map<string, Observable<Partial<IOption>[] | null>>();

  public updateCache(code: string, newValue: Partial<IOption>): void {
    const key = String(code);
    this.optionCache.set(key, of(newValue));
    this.optionsListCache.clear();
  }

  private _requestOption(code: OptionCode): Observable<Partial<IOption> | null> {
    const key = String(code);

    if (!this.optionCache.has(key)) {
      const params = new HttpParams().set('where[code]', code);

      const request$ = this.http
        .get<Partial<IOption>>(`${this.baseUrl}/value`, { params })
        .pipe(
          catchError(() => of(null)),
          shareReplay(1)
        );

      this.optionCache.set(key, request$);
    }

    return this.optionCache.get(key)!;
  }

  private _requestOptions(codes: OptionCode[]): Observable<Partial<IOption>[] | null> {
    const sortedCodes = [...codes].sort();
    const key = sortedCodes.join('|');

    if (!this.optionsListCache.has(key)) {
      const params = codes.reduce(
        (p, code) => p.append('where[code][]', code),
        new HttpParams(),
      );

      const request$ = this.http
        .get<Partial<IOption>[]>(`${this.baseUrl}/values`, { params })
        .pipe(
          catchError(() => of(null)),
          shareReplay(1)
        );

      this.optionsListCache.set(key, request$);
    }

    return this.optionsListCache.get(key)!;
  }

  public getOption(code: OptionCode): Signal<boolean | null | undefined> {
    return toSignal(
      this._requestOption(code).pipe(
        map((opt) => (opt ? Boolean(+opt.value!) : null)),
      ),
      { injector: this.injector, initialValue: undefined },
    );
  }

  public getOptionString(code: OptionCode): Signal<string | null | undefined> {
    return toSignal(
      this._requestOption(code).pipe(
        map((opt) => (opt ? opt.value : null))
      ),
      {
        injector: this.injector,
        initialValue: undefined,
      });
  }

  public getOptions(codes: OptionCode[]): Signal<boolean[] | null | undefined> {
    return toSignal(
      this._requestOptions(codes).pipe(
        map((opts) => opts ? opts.map((opt) => Boolean(+opt.value!)) : null),
      ),
      { injector: this.injector, initialValue: undefined },
    );
  }

  public rxGetOption(code: OptionCode): Observable<boolean | null> {
    return this._requestOption(code).pipe(
      map((opt) => (opt?.value ? Boolean(+opt.value) : null))
    );
  }

  public rxGetOptions(codes: OptionCode[]): Observable<boolean[] | null> {
    return this._requestOptions(codes).pipe(
      map((opts) => opts ? opts.map((opt) => Boolean(+opt.value!)) : null),
    );
  }

  public rxGetOptionString(code: OptionCode): Observable<string | null> {
    return this._requestOption(code).pipe(
      map((opt) => opt?.value ?? null)
    );
  }
}
